import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, BookOpen, Users, Clock, MoreHorizontal, Pencil, Trash2,
  AlertCircle, X, GraduationCap, User as UserIcon, Eye, Calendar, Wallet,
  Layers,
} from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function formatScheduleDay(day) {
  if (!day) return "—";
  if (ISO_DATE_RE.test(day)) {
    const d = new Date(`${day}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    }
  }
  return day.slice(0, 3);
}
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
];
const STATUS_STYLES = {
  active: "bg-[#DCFCE7] text-[#15803D]",
  inactive: "bg-[#F1F5F9] text-slate-500",
  archived: "bg-[#FFF3E0] text-[#E65100]",
};

export default function ClassesSectionsPage() {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const perms = user?.permissions || {};
  const canCreate = isOwner || !!perms.classes_create;
  const canEdit = isOwner || !!perms.classes_edit;
  const canDelete = isOwner || !!perms.classes_delete;
  const canViewEmployees = isOwner || !!perms.employees_view;

  const [tab, setTab] = useState("class");
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const branchId = selectedBranch?.id;

  const loadAll = useCallback(async () => {
    if (!branchId) { setClasses([]); setSections([]); setEmployees([]); return; }
    setLoading(true);
    try {
      const calls = [
        api.get("/classes", { params: { branch_id: branchId } }),
        api.get("/sections", { params: { branch_id: branchId } }),
      ];
      if (canViewEmployees) {
        calls.push(api.get("/employees", { params: { branch_id: branchId } }));
      }
      const results = await Promise.allSettled(calls);
      const [cRes, sRes, eRes] = results;
      if (cRes.status === "fulfilled") setClasses(cRes.value.data || []);
      else { toast.error(formatApiError(cRes.reason)); setClasses([]); }
      setSections(sRes.status === "fulfilled" ? (sRes.value.data || []) : []);
      setEmployees(eRes && eRes.status === "fulfilled" ? (eRes.value.data || []) : []);
    } finally { setLoading(false); }
  }, [branchId, canViewEmployees]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (!selectedBranch) {
    return (
      <>
        <PageHeader tab={tab} onAdd={() => {}} />
        <div className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-floating p-10 text-center max-w-xl" data-testid="no-branch-picked">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-[#E65100]" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
          <p className="text-slate-500 mt-1 text-sm">Classes and sections are scoped per branch.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        tab={tab}
        canAdd={canCreate}
        onAdd={() => {
          // Trigger Add for whichever tab is active
          window.dispatchEvent(new CustomEvent("classes-sections-add"));
        }}
      />
      <Tabs tab={tab} setTab={setTab} />
      {tab === "class" ? (
        <ClassTab branchId={branchId} classes={classes} sections={sections} currency={currency} reload={loadAll} loading={loading} canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />
      ) : (
        <SectionTab
          branchId={branchId} classes={classes} sections={sections}
          employees={employees} currency={currency} reload={loadAll} loading={loading}
          canCreate={canCreate} canEdit={canEdit} canDelete={canDelete}
        />
      )}
    </>
  );
}

function PageHeader({ tab, onAdd, canAdd = true }) {
  return (
    <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">Classes &amp; Sections</h1>
      </div>
      {canAdd && (
        <Button
          onClick={onAdd}
          className="rounded-full h-12 px-6 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white font-heading font-semibold shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)] hover:shadow-[0_14px_28px_-12px_rgba(37,99,235,0.70)] transition-all"
          data-testid={tab === "class" ? "page-add-class-btn" : "page-add-section-btn"}
        >
          <Plus className="w-4 h-4 mr-1.5" /> {tab === "class" ? "Add Class" : "Add Section"}
        </Button>
      )}
    </div>
  );
}

function Tabs({ tab, setTab }) {
  return (
    <div className="inline-flex p-1 rounded-full bg-white border border-[#2563EB]/10 shadow-sm mb-6" role="tablist">
      <button
        type="button" role="tab"
        onClick={() => setTab("class")}
        data-testid="tab-class"
        className={`px-5 h-10 rounded-full text-sm font-heading font-semibold transition flex items-center gap-2 ${
          tab === "class" ? "bg-[#DBEAFE] text-[#1D4ED8] shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]" : "text-slate-500 hover:text-foreground"
        }`}
      >
        Class
      </button>
      <button
        type="button" role="tab"
        onClick={() => setTab("section")}
        data-testid="tab-section"
        className={`px-5 h-10 rounded-full text-sm font-heading font-semibold transition flex items-center gap-2 ${
          tab === "section" ? "bg-[#DBEAFE] text-[#1D4ED8] shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]" : "text-slate-500 hover:text-foreground"
        }`}
      >
        Section
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* CLASS TAB                                                                  */
/* -------------------------------------------------------------------------- */
function ClassTab({ branchId, classes, sections, currency, reload, loading, canCreate = true, canEdit = true, canDelete = true }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const sectionsByClass = useMemo(() => {
    const m = {};
    for (const s of sections) m[s.class_id] = (m[s.class_id] || 0) + 1;
    return m;
  }, [sections]);

  // Listen to page header "Add" button
  useEffect(() => {
    const h = () => { setEditing(null); setOpen(true); };
    window.addEventListener("classes-sections-add", h);
    return () => window.removeEventListener("classes-sections-add", h);
  }, []);

  return (
    <div
      className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-floating"
      data-testid="class-tab"
    >
      {loading ? (
        <p className="p-8 text-slate-500">Loading...</p>
      ) : classes.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No classes yet"
          message="Create your first class to start organizing sections and students."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="class-table">
            <thead>
              <tr>
                <th>CLASS NAME</th>
                <th>TYPE</th>
                <th>SECTIONS</th>
                <th>FEE</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {classes.map((c) => (
                <tr key={c.id} data-testid={`class-row-${c.id}`}>
                  <td className="font-heading font-semibold text-foreground">{c.name}</td>
                  <td>
                    <ClassTypeChip type={c.class_type} />
                  </td>
                  <td className="text-slate-500 tabular-nums">
                    {c.class_type === "individual" ? "—" : (sectionsByClass[c.id] || 0)}
                  </td>
                  <td className="text-foreground font-heading tabular-nums whitespace-nowrap">
                    {c.class_type === "individual"
                      ? `${currency} ${Number(c.fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td><StatusChip status={c.status} /></td>
                  <td>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-slate-400 hover:text-[#1D4ED8] hover:bg-[#EFF6FF]" data-testid={`class-actions-${c.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        {canEdit && (
                          <DropdownMenuItem onClick={() => { setEditing(c); setOpen(true); }} data-testid={`class-edit-${c.id}`}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <DropdownMenuItem onClick={() => setDeleting(c)} className="text-[#C62828]" data-testid={`class-delete-${c.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legacy in-card Add button (kept invisible — page header drives it now) */}
      <button
        type="button"
        onClick={() => { setEditing(null); setOpen(true); }}
        className="sr-only"
        data-testid="add-class-btn"
        aria-label="Add Class"
      >
        Add Class
      </button>

      {open && (
        <ClassFormDialog
          open={open}
          onClose={() => { setOpen(false); setEditing(null); }}
          branchId={branchId}
          editing={editing}
          onSaved={() => { setOpen(false); setEditing(null); reload(); }}
        />
      )}
      <ConfirmDelete
        open={!!deleting}
        title="Delete this class?"
        description={
          deleting ? (
            (sectionsByClass[deleting.id] || 0) > 0
              ? `This will also delete ${sectionsByClass[deleting.id]} section(s) and their tuition history. This action cannot be undone.`
              : "This class will be permanently removed. This action cannot be undone."
          ) : ""
        }
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await api.delete(`/classes/${deleting.id}`);
            toast.success("Class deleted");
            setDeleting(null);
            reload();
          } catch (e) { toast.error(formatApiError(e)); }
        }}
      />
    </div>
  );
}

function ClassFormDialog({ open, onClose, branchId, editing, onSaved }) {
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";
  const [name, setName] = useState(editing?.name || "");
  const [classType, setClassType] = useState(editing?.class_type || "regular");
  const [fee, setFee] = useState(editing?.fee != null ? String(editing.fee) : "0");
  const [status, setStatus] = useState(editing?.status || "active");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Class name is required"); return; }
    const feeNum = Number(fee || 0);
    if (classType === "individual" && feeNum < 0) { toast.error("Fee cannot be negative"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        class_type: classType,
        status,
        fee: classType === "individual" ? feeNum : 0,
      };
      if (editing) {
        await api.patch(`/classes/${editing.id}`, payload);
        toast.success("Class updated");
      } else {
        await api.post("/classes", { branch_id: branchId, ...payload });
        toast.success("Class created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="class-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{editing ? "Edit class" : "Add class"}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">
            {editing ? "Update class details." : "Create a class for this branch. You can add sections under it from the Section tab."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-heading">Class Type <span className="text-[#C62828]">*</span></Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              <TypeOption
                active={classType === "regular"}
                onClick={() => setClassType("regular")}
                title="Regular Class"
                hint="Has many sections"
                icon={Users}
                testid="class-type-regular"
              />
              <TypeOption
                active={classType === "modular"}
                onClick={() => setClassType("modular")}
                title="Modular Class"
                hint="Self-paced modules"
                icon={Layers}
                testid="class-type-modular"
              />
              <TypeOption
                active={classType === "individual"}
                onClick={() => setClassType("individual")}
                title="Individual Session"
                hint="1-on-1 enrollment"
                icon={UserIcon}
                testid="class-type-individual"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="class-name" className="font-heading">Class Name <span className="text-[#C62828]">*</span></Label>
            <Input
              id="class-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Grade 1, Toddler, Speech Therapy 1-on-1"
              className="rounded-2xl h-11 mt-1.5"
              data-testid="class-name-input"
              autoFocus
            />
          </div>
          {classType === "individual" && (
            <div data-testid="class-fee-section">
              <Label htmlFor="class-fee" className="font-heading">Fee <span className="text-[#C62828]">*</span></Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkMuted">{currency}</span>
                <Input
                  id="class-fee" type="number" min="0" step="0.01" value={fee}
                  onChange={(e) => setFee(e.target.value)}
                  className="rounded-2xl h-11 pl-8 tabular-nums"
                  data-testid="class-fee-input"
                />
              </div>
              <p className="text-xs text-brand-inkMuted mt-1">Charged per enrollment. Auto-fills the student's tuition when this class is selected.</p>
            </div>
          )}
          <div>
            <Label className="font-heading">Status</Label>
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
              data-testid="class-status-select"
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11" data-testid="class-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white h-11 px-6 shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)]" data-testid="class-submit-btn">
              {saving ? "Saving..." : (editing ? "Save" : "Add")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TypeOption({ active, onClick, title, hint, icon: Icon, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={`p-3 rounded-2xl border text-left transition ${
        active ? "border-brand-purple bg-brand-purpleLight/40 shadow-sm" : "border-input hover:bg-brand-purpleLight/20"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`w-8 h-8 rounded-xl flex items-center justify-center ${active ? "bg-brand-purple text-white" : "bg-brand-purpleLight text-brand-purple"}`}>
          <Icon className="w-4 h-4" />
        </span>
        <div>
          <p className="font-heading font-semibold text-sm text-foreground">{title}</p>
          <p className="text-[11px] text-brand-inkMuted">{hint}</p>
        </div>
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* SECTION TAB                                                                */
/* -------------------------------------------------------------------------- */
function SectionTab({ branchId, classes, sections, employees, currency, reload, loading, canCreate = true, canEdit = true, canDelete = true }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [viewing, setViewing] = useState(null);

  // Regular, Modular, and legacy classes (null class_type) can have sections
  const regularClasses = useMemo(
    () => classes.filter((c) => c.class_type !== "individual"),
    [classes],
  );

  // Listen to page header "Add" button
  useEffect(() => {
    const h = () => {
      if (regularClasses.length === 0) {
        toast.error("Create a Regular or Modular Class first.");
        return;
      }
      setEditing(null); setOpen(true);
    };
    window.addEventListener("classes-sections-add", h);
    return () => window.removeEventListener("classes-sections-add", h);
  }, [regularClasses.length]);

  return (
    <div
      className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-floating"
      data-testid="section-tab"
    >
      {loading ? (
        <p className="p-8 text-slate-500">Loading...</p>
      ) : sections.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No sections yet"
          message={regularClasses.length === 0
            ? "Add a Regular or Modular Class first, then come back here to create sections."
            : "Add your first section to start enrolling students."}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="section-table">
            <thead>
              <tr>
                <th className="whitespace-nowrap">SECTION</th>
                <th className="whitespace-nowrap">CLASS / LEVEL</th>
                <th className="whitespace-nowrap">ADVISER</th>
                <th className="whitespace-nowrap">SCHEDULE</th>
                <th className="whitespace-nowrap">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => (
                <tr key={s.id} data-testid={`section-row-${s.id}`}>
                  <td className="font-heading font-semibold text-foreground whitespace-nowrap">{s.name}</td>
                  <td className="text-foreground whitespace-nowrap">{s.class_name || "—"}</td>
                  <td className="text-slate-500 whitespace-nowrap">{s.adviser_name || <span className="italic">No adviser assigned</span>}</td>
                  <td>
                    {(s.schedule || []).length === 0 ? <span className="text-slate-400 italic">—</span> : (
                      <div className="flex flex-col gap-1.5">
                        {s.schedule.map((sch, idx) => (
                          <span key={idx} className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1D4ED8] text-[11px] font-heading whitespace-nowrap">
                            <Clock className="w-3 h-3" />
                            {formatScheduleDay(sch.day)} {sch.time_start ? `${sch.time_start}–${sch.time_end || ""}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-slate-400 hover:text-[#1D4ED8] hover:bg-[#EFF6FF]" data-testid={`section-actions-${s.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        <DropdownMenuItem onClick={() => setViewing(s)} data-testid={`section-view-${s.id}`}>
                          <Eye className="w-4 h-4 mr-2" /> View
                        </DropdownMenuItem>
                        {canEdit && (
                          <DropdownMenuItem onClick={() => { setEditing(s); setOpen(true); }} data-testid={`section-edit-${s.id}`}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <DropdownMenuItem onClick={() => setDeleting(s)} className="text-[#C62828]" data-testid={`section-delete-${s.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legacy invisible button to keep prior test-ids working */}
      <button
        type="button"
        onClick={() => {
          if (regularClasses.length === 0) { toast.error("Create a Regular or Modular Class first."); return; }
          setEditing(null); setOpen(true);
        }}
        className="sr-only"
        data-testid="add-section-btn"
        aria-label="Add Section"
      >
        Add Section
      </button>

      {open && (
        <SectionFormDialog
          open={open}
          onClose={() => { setOpen(false); setEditing(null); }}
          branchId={branchId}
          regularClasses={regularClasses}
          employees={employees}
          currency={currency}
          editing={editing}
          onSaved={() => { setOpen(false); setEditing(null); reload(); }}
        />
      )}
      {viewing && (
        <SectionViewDialog
          section={viewing}
          currency={currency}
          onClose={() => setViewing(null)}
          onEdit={() => { setEditing(viewing); setViewing(null); setOpen(true); }}
        />
      )}
      <ConfirmDelete
        open={!!deleting}
        title="Delete this section?"
        description="This section and its tuition history will be permanently removed. This action cannot be undone."
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await api.delete(`/sections/${deleting.id}`);
            toast.success("Section deleted");
            setDeleting(null);
            reload();
          } catch (e) { toast.error(formatApiError(e)); }
        }}
      />
    </div>
  );
}

function SectionFormDialog({ open, onClose, branchId, regularClasses, employees, currency, editing, onSaved }) {
  const [classId, setClassId] = useState(editing?.class_id || regularClasses[0]?.id || "");
  const [name, setName] = useState(editing?.name || "");
  const [maxStudents, setMaxStudents] = useState(editing?.max_students ?? "");
  const [adviser, setAdviser] = useState(editing?.adviser_employee_id || "");
  const [tuition, setTuition] = useState(editing ? String(editing.tuition_fee ?? "0") : "0");
  const [status, setStatus] = useState(editing?.status || "active");
  const [schedule, setSchedule] = useState(
    editing?.schedule?.length ? editing.schedule.map((s) => ({ ...s })) : [{ day: "", time_start: "", time_end: "" }]
  );
  const [saving, setSaving] = useState(false);

  const isModular = useMemo(
    () => regularClasses.find((c) => c.id === classId)?.class_type === "modular",
    [regularClasses, classId],
  );

  const updateSlot = (idx, patch) => {
    setSchedule((arr) => arr.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };
  const addSlot = () => setSchedule((arr) => [...arr, { day: "", time_start: "", time_end: "" }]);
  const removeSlot = (idx) => setSchedule((arr) => arr.filter((_, i) => i !== idx));

  // --- Auto-generate date slots for modular cohorts -------------------------
  const [genOpen, setGenOpen] = useState(false);
  const [genStart, setGenStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [genInterval, setGenInterval] = useState("7"); // days between sessions
  const [genCount, setGenCount] = useState("8");
  const [genStartTime, setGenStartTime] = useState("09:00");
  const [genEndTime, setGenEndTime] = useState("11:00");
  const [genReplace, setGenReplace] = useState(true);

  const generateDateSlots = () => {
    const start = new Date(`${genStart}T00:00:00`);
    const interval = Math.max(1, Math.min(365, Number(genInterval) || 0));
    const count = Math.max(1, Math.min(200, Number(genCount) || 0));
    if (Number.isNaN(start.getTime())) { toast.error("Start date is invalid"); return; }
    const slots = [];
    for (let i = 0; i < count; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i * interval);
      slots.push({
        day: d.toISOString().slice(0, 10),
        time_start: genStartTime || "",
        time_end: genEndTime || "",
      });
    }
    setSchedule((arr) => {
      const kept = genReplace ? [] : arr.filter((s) => s.day && s.day.trim());
      return [...kept, ...slots];
    });
    setGenOpen(false);
    toast.success(`Generated ${count} session date${count === 1 ? "" : "s"}`);
  };
  // --------------------------------------------------------------------------

  const submit = async (e) => {
    e.preventDefault();
    if (!classId) { toast.error("Choose a class / level"); return; }
    if (!name.trim()) { toast.error("Section name is required"); return; }
    const cleanedSchedule = schedule
      .filter((s) => s.day && s.day.trim())
      .map((s) => ({
        day: s.day,
        time_start: s.time_start || null,
        time_end: s.time_end || null,
      }));
    const payload = {
      class_id: classId,
      name: name.trim(),
      max_students: maxStudents === "" || maxStudents === null ? null : Number(maxStudents),
      adviser_employee_id: adviser || null,
      tuition_fee: Number(tuition || 0),
      schedule: cleanedSchedule,
      status,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/sections/${editing.id}`, payload);
        toast.success("Section updated");
      } else {
        await api.post("/sections", { branch_id: branchId, ...payload });
        toast.success("Section created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="section-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{editing ? "Edit section" : "Add section"}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">Sections sit under Regular or Modular Classes and hold a roster of students.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-heading">Class / Level <span className="text-[#C62828]">*</span></Label>
            <select
              value={classId} onChange={(e) => setClassId(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
              data-testid="section-class-select"
            >
              <option value="">Select class / level</option>
              {regularClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="section-name" className="font-heading">Section Name <span className="text-[#C62828]">*</span></Label>
              <Input
                id="section-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Section A" className="rounded-2xl h-11 mt-1.5"
                data-testid="section-name-input"
              />
            </div>
            <div>
              <Label htmlFor="max-students" className="font-heading">Maximum Students</Label>
              <Input
                id="max-students" type="number" min="0" value={maxStudents}
                onChange={(e) => setMaxStudents(e.target.value)}
                className="rounded-2xl h-11 mt-1.5"
                data-testid="section-max-students-input"
              />
            </div>
          </div>

          <div>
            <Label className="font-heading">Adviser / Teacher</Label>
            <select
              value={adviser} onChange={(e) => setAdviser(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
              data-testid="section-adviser-select"
            >
              <option value="">No adviser assigned</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name}{emp.position ? ` — ${emp.position}` : ""}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="tuition" className="font-heading">Tuition Fee <span className="text-[#C62828]">*</span></Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkMuted">{currency}</span>
              <Input
                id="tuition" type="number" min="0" step="0.01" value={tuition}
                onChange={(e) => setTuition(e.target.value)}
                className="rounded-2xl h-11 pl-8 tabular-nums"
                data-testid="section-tuition-input"
              />
            </div>
            <p className="text-xs text-brand-inkMuted mt-1">Saved for the active school year. Past years remain unchanged.</p>
          </div>

          {/* Schedule */}
          <div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="font-heading flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-brand-purple" /> Schedule
              </Label>
              <div className="flex items-center gap-2">
                {isModular && (
                  <Button
                    type="button" variant="outline"
                    onClick={() => setGenOpen((v) => !v)}
                    className="rounded-xl h-9 px-3 text-sm border-brand-purple/40 text-brand-purple hover:bg-brand-purpleLight/50"
                    data-testid="generate-dates-btn"
                  >
                    <Calendar className="w-4 h-4 mr-1" /> {genOpen ? "Close generator" : "Generate dates"}
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={addSlot} className="rounded-xl h-9 px-3 text-sm" data-testid="add-schedule-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Schedule
                </Button>
              </div>
            </div>

            {isModular && genOpen && (
              <div
                className="mt-3 p-3 rounded-2xl border border-brand-purple/20 bg-brand-purpleLight/30 space-y-3"
                data-testid="date-generator-panel"
              >
                <p className="text-xs text-brand-inkMuted">Auto-fill session dates for this modular cohort. Existing dates can be replaced or kept.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs text-brand-inkMuted">Start date</Label>
                    <Input
                      type="date" value={genStart} onChange={(e) => setGenStart(e.target.value)}
                      className="rounded-xl h-10 mt-1"
                      data-testid="gen-start-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-brand-inkMuted">Repeat every (days)</Label>
                    <Input
                      type="number" min="1" max="365" value={genInterval}
                      onChange={(e) => setGenInterval(e.target.value)}
                      className="rounded-xl h-10 mt-1 tabular-nums"
                      data-testid="gen-interval-days"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-brand-inkMuted">Number of sessions</Label>
                    <Input
                      type="number" min="1" max="200" value={genCount}
                      onChange={(e) => setGenCount(e.target.value)}
                      className="rounded-xl h-10 mt-1 tabular-nums"
                      data-testid="gen-session-count"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-brand-inkMuted">Preset</Label>
                    <select
                      value={genInterval}
                      onChange={(e) => setGenInterval(e.target.value)}
                      className="w-full h-10 mt-1 rounded-xl border border-input bg-white px-2.5 text-sm"
                      data-testid="gen-preset"
                    >
                      <option value="1">Daily</option>
                      <option value="7">Weekly</option>
                      <option value="14">Every 2 weeks</option>
                      <option value="30">Monthly</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-brand-inkMuted">Time start</Label>
                    <Input
                      type="time" value={genStartTime} onChange={(e) => setGenStartTime(e.target.value)}
                      className="rounded-xl h-10 mt-1"
                      data-testid="gen-time-start"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-brand-inkMuted">Time end</Label>
                    <Input
                      type="time" value={genEndTime} onChange={(e) => setGenEndTime(e.target.value)}
                      className="rounded-xl h-10 mt-1"
                      data-testid="gen-time-end"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-xs text-brand-inkMuted cursor-pointer">
                    <input
                      type="checkbox" checked={genReplace}
                      onChange={(e) => setGenReplace(e.target.checked)}
                      className="rounded"
                      data-testid="gen-replace-toggle"
                    />
                    Replace existing schedule
                  </label>
                  <Button
                    type="button"
                    onClick={generateDateSlots}
                    className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] text-white h-10 px-5"
                    data-testid="gen-generate-btn"
                  >
                    Generate
                  </Button>
                </div>
              </div>
            )}
            <div className="mt-2 space-y-2">
              {schedule.length === 0 && (
                <p className="text-xs text-brand-inkMuted italic">No schedule yet — click <span className="font-semibold">Add Schedule</span> to add a slot.</p>
              )}
              {schedule.map((slot, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end" data-testid={`schedule-row-${idx}`}>
                  <div>
                    {idx === 0 && <Label className="text-xs text-brand-inkMuted">{isModular ? "Date" : "Day"}</Label>}
                    {isModular ? (
                      <Input
                        type="date" value={slot.day || ""} onChange={(e) => updateSlot(idx, { day: e.target.value })}
                        className="rounded-xl h-10 mt-1"
                        data-testid={`schedule-day-${idx}`}
                      />
                    ) : (
                      <select
                        value={slot.day || ""} onChange={(e) => updateSlot(idx, { day: e.target.value })}
                        className="w-full h-10 rounded-xl border border-input bg-white px-2.5 text-sm mt-1"
                        data-testid={`schedule-day-${idx}`}
                      >
                        <option value="">Select day</option>
                        {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    )}
                  </div>
                  <div>
                    {idx === 0 && <Label className="text-xs text-brand-inkMuted">Time Start</Label>}
                    <Input
                      type="time" value={slot.time_start || ""} onChange={(e) => updateSlot(idx, { time_start: e.target.value })}
                      className="rounded-xl h-10 mt-1"
                      data-testid={`schedule-start-${idx}`}
                    />
                  </div>
                  <div>
                    {idx === 0 && <Label className="text-xs text-brand-inkMuted">Time End</Label>}
                    <Input
                      type="time" value={slot.time_end || ""} onChange={(e) => updateSlot(idx, { time_end: e.target.value })}
                      className="rounded-xl h-10 mt-1"
                      data-testid={`schedule-end-${idx}`}
                    />
                  </div>
                  <Button
                    type="button" variant="ghost" size="icon"
                    onClick={() => removeSlot(idx)}
                    className="h-10 w-10 rounded-xl text-brand-inkMuted hover:text-[#C62828]"
                    data-testid={`schedule-remove-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="font-heading">Status</Label>
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
              data-testid="section-status-select"
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11" data-testid="section-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white h-11 px-6 shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)]" data-testid="section-submit-btn">
              {saving ? "Saving..." : (editing ? "Save" : "Add")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Section View Dialog — full section details                                 */
/* -------------------------------------------------------------------------- */
function SectionViewDialog({ section, currency, onClose, onEdit }) {
  const s = section;
  const tuition = `${currency} ${Number(s.tuition_fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const hasSchedule = (s.schedule || []).length > 0;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-xl rounded-3xl p-0 max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="section-view-dialog"
      >
        {/* Hero header */}
        <div className="relative bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] text-white px-6 sm:px-7 pt-7 pb-6">
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <p className="text-xs uppercase tracking-wider opacity-80 font-heading">Section</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold mt-0.5">{s.name}</h2>
            <p className="text-sm opacity-90 mt-1">
              {s.class_name || "—"}
              {s.class_type === "individual" && <span className="ml-2 text-[11px] uppercase tracking-wider bg-white/20 px-1.5 py-0.5 rounded">Individual</span>}
              {s.class_type === "modular" && <span className="ml-2 text-[11px] uppercase tracking-wider bg-white/20 px-1.5 py-0.5 rounded">Modular</span>}
            </p>
            <div className="mt-3 inline-flex">
              <StatusChip status={s.status} />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 sm:px-7 py-5 overflow-y-auto space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoCard icon={UserIcon} label="Adviser / Teacher">
              {s.adviser_name || <span className="italic text-brand-inkMuted">No adviser assigned</span>}
            </InfoCard>
            <InfoCard icon={Users} label="Maximum Students">
              <span className="tabular-nums">{s.max_students ?? "—"}</span>
            </InfoCard>
            <InfoCard icon={Wallet} label="Tuition Fee" className="sm:col-span-2">
              <span className="font-heading text-lg text-foreground tabular-nums">{tuition}</span>
              <p className="text-[11px] text-brand-inkMuted mt-0.5">For the active school year.</p>
            </InfoCard>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-xl bg-brand-purpleLight text-brand-purple flex items-center justify-center">
                <Calendar className="w-4 h-4" />
              </div>
              <p className="font-heading font-semibold text-foreground">Schedule</p>
            </div>
            {hasSchedule ? (
              <ul className="space-y-1.5" data-testid="view-schedule-list">
                {s.schedule.map((sch, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-2xl bg-brand-purpleLight/40 border border-brand-purple/10"
                  >
                    <span className="font-heading font-semibold text-foreground text-sm">{sch.day ? (ISO_DATE_RE.test(sch.day) ? new Date(`${sch.day}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : sch.day) : "—"}</span>
                    <span className="text-sm tabular-nums text-brand-inkMuted whitespace-nowrap">
                      <Clock className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-brand-purple" />
                      {sch.time_start || "—"} {sch.time_end ? `– ${sch.time_end}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-3 py-4 rounded-2xl border border-dashed border-brand-purple/20 text-sm text-brand-inkMuted italic">
                No schedule added yet.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-7 py-4 border-t border-brand-purple/10 bg-[#FAFBFE] flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-2xl h-11 sm:h-10" data-testid="section-view-close-btn">
            Close
          </Button>
          <Button onClick={onEdit} className="rounded-full bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white h-11 sm:h-10 px-5 shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)]" data-testid="section-view-edit-btn">
            <Pencil className="w-4 h-4 mr-1.5" /> Edit Section
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoCard({ icon: Icon, label, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-brand-purple/10 bg-white px-3.5 py-3 ${className}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-lg bg-brand-purpleLight text-brand-purple flex items-center justify-center">
          <Icon className="w-3.5 h-3.5" />
        </div>
        <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p>
      </div>
      <div className="text-sm text-foreground pl-9">{children}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable bits                                                              */
/* -------------------------------------------------------------------------- */
function StatusChip({ status }) {
  const styles = STATUS_STYLES[status] || STATUS_STYLES.inactive;
  const label = STATUS_OPTIONS.find((o) => o.value === status)?.label || status;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-heading font-semibold ${styles}`}>
      {label}
    </span>
  );
}

function ClassTypeChip({ type }) {
  if (type === "individual") {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-heading font-semibold bg-[#FFE8D6] text-[#D97706]">
        Individual Session
      </span>
    );
  }
  if (type === "modular") {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-heading font-semibold bg-[#EDE9FE] text-[#6D28D9]">
        Modular Class
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-heading font-semibold bg-[#DBEAFE] text-[#1D4ED8]">
      Regular Class
    </span>
  );
}

function EmptyState({ icon: Icon, title, message }) {
  return (
    <div className="p-10 text-center" data-testid="empty-state">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
        <Icon className="w-7 h-7 text-brand-purple" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">{title}</p>
      <p className="text-brand-inkMuted mt-1 text-sm">{message}</p>
    </div>
  );
}

function ConfirmDelete({ open, title, description, onCancel, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent className="rounded-3xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-brand-inkMuted">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="rounded-2xl bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="confirm-delete-btn">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
